// ── IP 관리 — 데이터 상태 훅 ──────────────────────────────────────────────────
// 상태 선언, 파생 값(useMemo), 초기 데이터 로드(fetchAll), resize 감지
import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  PAGE_SIZE, getIsMobile, normalizeIp, isLoopbackIp, isActiveRow,
} from './utils';

/**
 * IP 관리 데이터 상태 훅
 * @param {object} params
 *   showToast — 토스트 알림 함수
 *   apiBase   — 창을 연 출퇴근 화면의 범위 주소(/api/admin/ip-scope/...). 목록·접속자 모두 서버가 그 범위로 거른다.
 */
export function useIpData({ showToast, apiBase }) {
  // ── 레이아웃 ────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(getIsMobile());

  // ── 데이터 목록 ──────────────────────────────────────────────────────────
  const [list,       setList]       = useState([]);          // IP 연결 목록
  const [onlineUsers, setOnlineUsers] = useState([]);       // 최근 활동 사용자
  const [employees,  setEmployees]  = useState([]);         // 직원 목록 (등록 폼용)
  const [aliasRows,  setAliasRows]  = useState([]);         // IP 별칭 목록

  // ── 현재 관리자 IP ────────────────────────────────────────────────────
  const [currentIp,     setCurrentIp]     = useState('');
  const [currentIpSource, setCurrentIpSource] = useState('');

  // ── 비지 상태 (API 요청 중 표시용) ────────────────────────────────────
  const [busyKey,            setBusyKey]            = useState('');   // 온라인 사용자 등록 버튼
  const [addBusy,            setAddBusy]            = useState(false); // 폼 등록 버튼
  const [descBusyId,         setDescBusyId]         = useState(null);  // 설명 저장 버튼
  const [aliasBusyIp,        setAliasBusyIp]        = useState('');   // 별칭 저장 버튼
  const [aliasDeleteBusyIp,  setAliasDeleteBusyIp]  = useState('');  // 별칭 삭제 버튼

  // ── 인라인 편집 드래프트 ─────────────────────────────────────────────
  const [descDraftById,  setDescDraftById]  = useState({}); // row.id → description 텍스트
  const [aliasDraftByIp, setAliasDraftByIp] = useState({}); // ip → alias 텍스트

  // ── 페이지네이션 ─────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [onlinePage,  setOnlinePage]  = useState(1);

  // ── 추가 폼 ──────────────────────────────────────────────────────────
  const [addForm, setAddForm] = useState({ ip_address: '', description: '', user_id: '', label: '' });

  // ── 파생 값 ──────────────────────────────────────────────────────────
  const totalPages       = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const onlineTotalPages = Math.max(1, Math.ceil(onlineUsers.length / PAGE_SIZE));

  // 현재 페이지 IP 연결 목록
  const pagedList = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [currentPage, list]);

  // 현재 페이지 최근 활동 사용자
  const pagedOnlineUsers = useMemo(() => {
    const start = (onlinePage - 1) * PAGE_SIZE;
    return onlineUsers.slice(start, start + PAGE_SIZE);
  }, [onlinePage, onlineUsers]);

  // 직원 전용 활성 연결의 "userId|ip" 집합 (빠른 등록 여부 판정용)
  const activeKeySet = useMemo(() => {
    const keys = new Set();
    for (const row of list) {
      if (!isActiveRow(row) || !row.user_id) continue;
      const ip = normalizeIp(row.ip_address);
      if (!ip || isLoopbackIp(ip)) continue;
      keys.add(`${Number(row.user_id)}|${ip}`);
    }
    return keys;
  }, [list]);

  // 공용 활성 연결 IP 집합
  const activePublicIpSet = useMemo(() => {
    const ips = new Set();
    for (const row of list) {
      if (!isActiveRow(row) || row.user_id) continue;
      const ip = normalizeIp(row.ip_address);
      if (!ip || isLoopbackIp(ip)) continue;
      ips.add(ip);
    }
    return ips;
  }, [list]);

  // ip → 별칭 Map (별칭 테이블 우선, 없으면 연결 목록 label로 보완)
  const aliasByIp = useMemo(() => {
    const map = new Map();
    for (const row of aliasRows) {
      const ip = normalizeIp(row.ip_address);
      if (!ip) continue;
      map.set(ip, String(row.alias || '').trim());
    }
    for (const row of list) {
      const ip = normalizeIp(row.ip_address);
      if (!ip || map.has(ip)) continue;
      map.set(ip, String(row.label || '').trim());
    }
    return map;
  }, [aliasRows, list]);

  // 중복 제거된 별칭 행 목록 (ip별 첫 번째만)
  const uniqueAliasRows = useMemo(() => {
    const map = new Map();
    for (const row of aliasRows) {
      const ip = normalizeIp(row.ip_address);
      if (!ip || isLoopbackIp(ip) || map.has(ip)) continue;
      map.set(ip, row);
    }
    return Array.from(map.values());
  }, [aliasRows]);

  // ── 초기 데이터 로드 (API 병렬 호출) ─────────────────────────────────
  async function fetchAll() {
    // 접속 중인 사람도 범위로 거른 목록(회사 화면 = 그 회사 사람만, 통합 = 묶인 회사 전부, 2026-10-23)
    const [ipRes, listRes, aliasRes, onlineRes, usersRes] = await Promise.all([
      apiClient.get('/api/my-ip'),
      apiClient.get(`${apiBase}/ips`),
      apiClient.get(`${apiBase}/ip-aliases`),
      apiClient.get(`${apiBase}/online-users`),
      // IP 화면 전용 경량 직원 목록 — 범위 회사의 재직 직원만(마케터 제외)
      apiClient.get(`${apiBase}/ips/employees`),
    ]);

    // 현재 IP
    if (ipRes.ok) {
      setCurrentIp(normalizeIp(ipRes.data?.ip || '') || '-');
      setCurrentIpSource(String(ipRes.data?.source || ''));
    } else {
      setCurrentIp('-');
      setCurrentIpSource('');
    }

    // IP 연결 목록 (루프백 제외)
    if (listRes.ok) {
      const rows = (Array.isArray(listRes.data) ? listRes.data : []).filter((row) => {
        const ip = normalizeIp(row.ip_address);
        return ip && !isLoopbackIp(ip);
      });
      setList(rows);
      // 설명 드래프트 초기화
      setDescDraftById(rows.reduce((acc, row) => { acc[row.id] = row.description || ''; return acc; }, {}));
    } else {
      showToast(listRes.data?.message || 'IP 연결 목록 조회 실패', 'error');
    }

    // IP 별칭 목록 (루프백 제외)
    if (aliasRes.ok) {
      const rows = (Array.isArray(aliasRes.data) ? aliasRes.data : []).filter((row) => {
        const ip = normalizeIp(row.ip_address);
        return ip && !isLoopbackIp(ip);
      });
      setAliasRows(rows);
      // 별칭 드래프트 초기화
      setAliasDraftByIp(rows.reduce((acc, row) => {
        acc[normalizeIp(row.ip_address)] = row.alias || '';
        return acc;
      }, {}));
    } else {
      showToast(aliasRes.data?.message || 'IP 별칭 목록 조회 실패', 'error');
    }

    // 최근 활동 사용자 (루프백 제외)
    if (onlineRes.ok) {
      const rows = (Array.isArray(onlineRes.data) ? onlineRes.data : []).filter((row) => {
        const ip = normalizeIp(row.ip_address);
        return ip && !isLoopbackIp(ip);
      });
      setOnlineUsers(rows);
    } else {
      showToast(onlineRes.data?.message || '최근 활동 조회 실패', 'error');
    }

    // 직원 목록 (전용 API — 활성 직원 id·name·user_id만, 이름 가나다순)
    if (usersRes.ok) {
      const users = Array.isArray(usersRes.data?.list) ? usersRes.data.list : [];
      setEmployees(
        [...users].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko-KR'))
      );
    }
    // 실패 시 토스트 없이 빈 목록 유지 — 보조 드롭다운일 뿐 페이지를 깨지 않게 (useAttendanceData IP 처리와 동일 관례)
  }

  // ── useEffects ───────────────────────────────────────────────────────
  // 범위가 바뀌면(다른 회사 화면에서 다시 열기) 다시 불러온다
  useEffect(() => { fetchAll(); }, [apiBase]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrentPage((p) => Math.min(Math.max(1, p), totalPages)); }, [totalPages]);
  useEffect(() => { setOnlinePage((p) => Math.min(Math.max(1, p), onlineTotalPages)); }, [onlineTotalPages]);
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── 폼 헬퍼 ─────────────────────────────────────────────────────────
  /** addForm 단일 필드 변경 */
  function setAddField(key, value) {
    setAddForm((prev) => ({ ...prev, [key]: value }));
  }

  return {
    // 레이아웃
    isMobile,
    // 데이터
    list, onlineUsers, employees, aliasRows,
    currentIp, currentIpSource,
    // 비지 상태 + 세터
    busyKey, setBusyKey, addBusy, setAddBusy,
    descBusyId, setDescBusyId, aliasBusyIp, setAliasBusyIp,
    aliasDeleteBusyIp, setAliasDeleteBusyIp,
    // 드래프트 + 세터
    descDraftById, setDescDraftById, aliasDraftByIp, setAliasDraftByIp,
    // 페이지네이션
    currentPage, setCurrentPage, totalPages,
    onlinePage, setOnlinePage, onlineTotalPages,
    // 파생 값
    pagedList, pagedOnlineUsers, activeKeySet, activePublicIpSet, aliasByIp, uniqueAliasRows,
    // 폼
    addForm, setAddForm, setAddField,
    // 데이터 갱신
    fetchAll,
  };
}
